import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { TypeParams } from '../../../types'
import type { Plugin } from '../plugin'

const SOURCE = 'applyPublishedInfoEntries (effect)'

/**
 * Keeps the entries the conversation's messages are made of up to date from
 * what the backend publishes.
 */
export const applyPublishedInfoEntries = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) => {
    /**
     * Recovers from storage the entries of a message that no publication
     * delivered here, leaving the conversation's loading state alone.
     */
    const recoverInfo = (
      context: TypeParams['context'],
      messageId: ents.core.agent.Message['id'],
    ): rxjs.Observable<store.actions.Action.Constraint> =>
      plugin.api.list(context).pipe(
        rxjs.concatMap(messages => {
          const listed = messages.find(message => message.id === messageId)
          if (!listed) {
            return rxjs.EMPTY
          }

          return rxjs.from([
            // Only the entries, so a status published during the read is kept.
            plugin.collection.plugins.cachePlugin.feature.actions.patch
              .from(SOURCE)
              .create({
                id: messageId,
                update: cached =>
                  cached ? { ...cached, info: listed.info } : listed,
              }),
            // Lists the message if its own publication was missed too.
            plugin.collection.plugins.currentListPlugin.feature.actions.insert
              .from(SOURCE)
              .create({ ids: [messageId] }),
          ])
        }),
        // The next entry past the gap retries.
        rxjs.catchError(() => rxjs.EMPTY),
      )

    /**
     * Takes in a published entry, and recovers its message's entries when it
     * lands past a gap.
     */
    const putInfoEntry = (
      context: TypeParams['context'],
      entry: ents.core.agent.Info.Stored,
    ): rxjs.Observable<store.actions.Action.Constraint> =>
      rxjs
        .combineLatest([
          plugin.collection.plugins.cachePlugin.feature.selectors.lookup
            .asObservable,
          feature.selectors.infoByMessageId.asObservable,
        ])
        .pipe(
          rxjs.take(1),
          rxjs.concatMap(([cached, infoByMessageId]) => {
            const put = rxjs.of(
              feature.actions.putInfoEntries
                .from(SOURCE)
                .create({ entries: [entry] }),
            )
            const { gap } = ents.core.agent.Info.Stored.merge(
              cached[entry.messageId]?.info ?? [],
              [...(infoByMessageId[entry.messageId] ?? []), entry],
            )

            return gap
              ? rxjs.concat(put, recoverInfo(context, entry.messageId))
              : put
          }),
        )

    // Updates are scoped to one conversation, so the subscription follows the
    // collection's context: switchMap drops the previous conversation's when
    // the user moves to another.
    return plugin.collection.feature.selectors.context.asObservable.pipe(
      rxjs.switchMap(context =>
        plugin.api.onInfoEntry(context).pipe(
          // One at a time, so a gap starts a single read and later entries
          // are checked against its result.
          rxjs.concatMap(entry => putInfoEntry(context, entry)),
        ),
      ),
    )
  },
)
